import { CheckIcon, FileTextIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import useDatasources from '@/hooks/useDatasources';
import useDocuments from '@/hooks/useDocuments';
import { DATASOURCE_TYPE_TO_TAB, ROOT_DATASOURCE_NAMES } from '@/modules/collections/constants';
import { datasourceIconByType } from '@/modules/tryIt/components/Messages/MessageNode/MessageNodeHeader';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Datasource } from '@/shared/types/collections';

import { OutcomeBadge } from './StepRows';

type DocumentLineProps = {
  collectionId: string;
  datasourceId: string;
  documentId: string;
  name?: string;
  score?: number;
  isSelected?: boolean;
};

const Row = ({
  icon,
  children,
  trailing,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  trailing?: React.ReactNode;
}) => (
  <li className="flex min-h-5 items-center justify-between gap-2 self-stretch text-sm leading-5 text-foreground-muted">
    <div className="flex min-w-0 items-center justify-center gap-1">
      <span className="flex size-4 shrink-0 items-center justify-center">{icon}</span>
      {children}
    </div>
    {trailing}
  </li>
);

const StatusPill = ({ isDraft }: { isDraft: boolean }) => {
  const { t } = useTranslation();

  return (
    <InfoTooltip
      className="z-3000"
      content={
        isDraft
          ? t('collections.status_tooltip.draft_testing_context')
          : t('collections.status_tooltip.published')
      }
      side="top"
    >
      <OutcomeBadge flavour={isDraft ? 'neutral' : 'successful'}>
        {isDraft ? t('collections.is_draft') : t('collections.published')}
      </OutcomeBadge>
    </InfoTooltip>
  );
};

const SelectedCheck = () => {
  const { t } = useTranslation();

  return (
    <CheckIcon
      role="img"
      aria-label={t('try_it.details.source_selected')}
      className="size-4 shrink-0 text-text-success-firm"
    />
  );
};

const Outcome = ({ isDraft, score }: { isDraft: boolean; score?: number }) => (
  <div className="flex shrink-0 items-center gap-1.5">
    <StatusPill isDraft={isDraft} />
    {score !== undefined && (
      <>
        <span className="text-xs leading-5 text-foreground-muted">·</span>
        <span className="w-8 shrink-0 text-right font-mono text-xs leading-4 font-normal text-foreground-muted">
          {score.toFixed(2)}
        </span>
      </>
    )}
  </div>
);

const documentLink = (
  slug: string,
  collectionId: string,
  datasourceId: string,
  documentId: string,
  datasource: Datasource
) => {
  const category = DATASOURCE_TYPE_TO_TAB[datasource.type];
  const hash = `document-${documentId}`;
  const isRoot = !!(Object.values(ROOT_DATASOURCE_NAMES) as string[]).includes(datasource.name);

  if (category === 'webpages' || category === 'external') {
    return `/${slug}/knowledge-bases/${collectionId}/category/${category}/${datasourceId}#${hash}`;
  }
  const folder = isRoot ? '' : datasourceId;
  return `/${slug}/knowledge-bases/${collectionId}/category/${category}/${folder}#${hash}`;
};

export const useDocumentLink = (collectionId: string, datasourceId: string, documentId: string) => {
  const slug = useSelector(selectAccountSlug);
  const { datasources } = useDatasources(collectionId);
  const datasource = datasources?.find((entry) => entry.datasource_id === datasourceId);

  if (!slug || !datasource) {
    return null;
  }

  return documentLink(slug, collectionId, datasourceId, documentId, datasource);
};

export const DocumentLine = ({
  collectionId,
  datasourceId,
  documentId,
  name,
  score,
  isSelected = false,
}: DocumentLineProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);

  const { datasources, listStatus } = useDatasources(collectionId);
  const datasource = datasources?.find((entry) => entry.datasource_id === datasourceId);
  const { document, getStatus: documentStatus } = useDocuments(
    collectionId,
    datasource?.datasource_id,
    documentId
  );

  if (!collectionId || !documentId) {
    return null;
  }

  if (!datasourceId) {
    return (
      <Row icon={<FileTextIcon size={16} />}>
        <span className="min-w-0 truncate text-sm leading-none font-medium text-foreground">
          {name ?? t('activityLogs.document_deleted')}
        </span>
        {isSelected && <SelectedCheck />}
      </Row>
    );
  }

  if (listStatus === 'pending') {
    return null;
  }

  if (!datasource) {
    return (
      <Row icon={<FileTextIcon size={16} />}>
        <span className="min-w-0 flex-1">{t('activityLogs.document_deleted')}</span>
      </Row>
    );
  }

  const icon = datasourceIconByType[datasource.type];

  if (documentStatus === 'pending') {
    return null;
  }

  if (!document) {
    return (
      <Row icon={icon}>
        <span className="min-w-0 flex-1 truncate">
          {t('activityLogs.deleted_from')}{' '}
          <InfoTooltip className="z-3000" content={t('try_it.details.go_to_datasource')}>
            <NavLink
              className="cursor-pointer text-informational hover:text-accent"
              target="_blank"
              rel="noopener noreferrer"
              to={`/${slug}/knowledge-bases/${collectionId}/datasources/${datasourceId}`}
            >
              {datasource.name}
            </NavLink>
          </InfoTooltip>
        </span>
      </Row>
    );
  }

  return (
    <Row icon={icon} trailing={<Outcome isDraft={document.is_draft} score={score} />}>
      <InfoTooltip className="z-3000" content={t('try_it.details.go_to_document')}>
        <span className="min-w-0 truncate">
          <NavLink
            className="cursor-pointer text-sm leading-none font-medium text-foreground hover:text-informational hover:underline"
            target="_blank"
            rel="noopener noreferrer"
            to={documentLink(slug, collectionId, datasourceId, documentId, datasource)}
          >
            {document.name}
          </NavLink>
        </span>
      </InfoTooltip>
      {isSelected && <SelectedCheck />}
    </Row>
  );
};
